<template>
    <layout-logged-in :accessType="accessType" :doneRendering="doneRendering" :showBackButton="true">
        <div :id="id" class="car-itinerary-details">
            <AlertDialog :id="'car-shipment-details-updated-' + carItineraryId"
                         v-model="visibleCarShipmentDetailsUpdateSuccess">Successfully
                updated car shipment details
            </AlertDialog>

            <AlertDialog title="Error" :id="'car-shipment-details-update-failed-' + carItineraryId"
                         v-model="visibleCarShipmentDetailsUpdateFailure">Failed
                to update shipment details. Could be a technical issue, please try again later.
            </AlertDialog>

            <h2>Itinerary details: {{ carItineraryHelper.getTitle() }}</h2>
            <v-container>
                <v-layout>
                    <v-flex>
                        <DriverPickUp :carItineraryHelper="carItineraryHelper"
                                      :accessType="accessType"
                                      :encryptedData="encryptedData"
                                      ref="driverPickUp">
                        </DriverPickUp>
                    </v-flex>
                </v-layout>
                <v-layout class="mt-2">
                    <v-flex>
                        <DriverUnloading
                                @unloadingConfirmed="unloadingConfirmed"
                                @unloadingCancelled="unloadingCancelled"
                                :carItineraryHelper="carItineraryHelper"
                                :accessType="accessType"
                                :encryptedData="encryptedData"
                                ref="driverUnloading">
                        </DriverUnloading>
                    </v-flex>
                </v-layout>
            </v-container>

            <v-container class="content">
                <v-layout>
                    <v-flex class="md12">
                        <span class="name">Loading address:</span> {{ details.pickUpAddress }}
                    </v-flex>
                </v-layout>
                <v-layout class="mt-2">
                    <v-flex class="md12">
                        <span class="name">Unloading address:</span> {{ details.dropOffAddress }}
                    </v-flex>
                </v-layout>
            </v-container>

            <br/>
            <v-container class="content shipments">
                <v-layout class="header">
                    <v-flex class="xs4 sm4 md4 data-cell awb">
                        AWB
                    </v-flex>
                    <v-flex class="xs1 sm1 md1 data-cell pieces">
                        Pcs
                    </v-flex>
                    <v-flex class="xs2 sm2 md2 data-cell weight">
                        Act.wt
                    </v-flex>
                    <v-flex class="xs2 sm2 md2 data-cell volume">
                        VOL
                    </v-flex>
                    <v-flex class="xs3 sm3 md3 data-cell shc">
                        SHC
                    </v-flex>
                </v-layout>

                <v-layout class="shipment" :key="index" v-for="(shipment, index) in details.shipments">
                    <v-flex :class="{'xs4 sm4 md4 data-cell awb': true, 'shipment-edit': showShipmentDetailsEdit(shipment)}">
                        {{ shipment.awb }}
                    </v-flex>
                    <v-flex :class="{'xs1 sm1 md1 data-cell pieces': true, 'data-cell-edit': showShipmentDetailsEdit(shipment)}">
                        <EditShipmentNumberField :carItineraryHelper="carItineraryHelper"
                                                 :shipment="shipment"
                                                 :shipmentId="shipment.id"
                                                 name="pieces"
                                                 v-model.number="shipment.pieces"
                                                 :textValue="shipment.pieces + ''"
                                                 @input="onPiecesInput"></EditShipmentNumberField>
                    </v-flex>
                    <v-flex :class="{'xs2 sm2 md2 data-cell weight': true, 'data-cell-edit': showShipmentDetailsEdit(shipment)}">
                        <EditShipmentNumberField :carItineraryHelper="carItineraryHelper"
                                                 :shipment="shipment"
                                                 :shipmentId="shipment.id"
                                                 name="weight"
                                                 :value="shipment.weight"
                                                 :textValue="shipment.weight | oneDecimal | thousandFormat"
                                                 @input="onWeightInput"
                        ></EditShipmentNumberField>
                    </v-flex>
                    <v-flex :class="{'xs2 sm2 md2 data-cell volume': true, 'shipment-edit': showShipmentDetailsEdit(shipment)}">
                        {{ shipment.volume | twoDecimal }}
                    </v-flex>
                    <v-flex :class="{'xs3 sm3 md3 data-cell shc': true, 'shipment-edit': showShipmentDetailsEdit(shipment)}">
                        {{ shipment.shcs.join(' ') }}
                    </v-flex>
                </v-layout>

                <v-layout class="totals">
                    <v-flex class="xs4 sm4 md4 data-cell awb">
                    </v-flex>
                    <v-flex class="xs1 sm1 md1 data-cell pieces">
                        {{ details.shipmentTotals.pieces }}
                    </v-flex>
                    <v-flex class="xs2 sm2 md2 data-cell weight">
                        {{ details.shipmentTotals.weight | oneDecimal | thousandFormat }}
                    </v-flex>
                    <v-flex class="xs2 sm2 md2 data-cell volume">
                        {{ details.shipmentTotals.volume | twoDecimal }}
                    </v-flex>
                    <v-flex class="xs3 sm3 md3 data-cell shc">

                    </v-flex>
                </v-layout>

                <v-layout v-if="showDetailsEdit()">
                    <v-flex class="action-bar">
                        <v-btn color="primary" @click="save">Save</v-btn>
                    </v-flex>
                </v-layout>
            </v-container>
        </div>
        <AlertDialog :id="'failed-details-' + carItineraryId" v-model="showDetailsFailed">Failed to load car itinerary
            details
        </AlertDialog>
    </layout-logged-in>
</template>

<script>

import AccessType from '@/components/Core/AccessType'
import DriverPickUp from '@/components/CarItinerary/PickUp/PickUpToggle/DriverPickUp'
import DriverUnloading from '@/components/CarItinerary/Delivery/DriverUnloadingToggle/DriverUnloading'
import QStepApi from '@/libraries/Api/QStep/Api'
import CarItineraryHelper from '@/libraries/Models/CarItinerary'
import Float from '@/libraries/Util/Float'
import AlertDialog from '@/components/Core/Dialog/Alert'
import LayoutLoggedIn from '@/components/Layout/LoggedIn'
import EditShipmentNumberField from '@/components/CarItinerary/EditShipmentNumberField'

export default {
  extends: AccessType,
  components: {
    DriverPickUp, DriverUnloading, AlertDialog, LayoutLoggedIn, EditShipmentNumberField
  },
  props: {
    carItineraryId: {
      type: Number,
      required: true
    }
  },
  name: 'Details',
  data () {
    return {
      doneRendering: false,
      showDetailsFailed: false,
      carItinerary: null,
      id: 'car-itinerary-details-' + this.carItineraryId,
      details: {
        pickUpAddress: '',
        dropOffAddress: '',
        notice: '',
        shipments: [],
        shipmentTotals: {
          pieces: '',
          weight: '',
          volume: ''
        }
      },
      visibleCarShipmentDetailsUpdateSuccess: false,
      visibleCarShipmentDetailsUpdateFailure: false
    }
  },
  mounted () {
    this.updateDetails()
  },
  computed: {
    carItineraryHelper () {
      return new CarItineraryHelper(this.carItinerary)
    }
  },
  filters: {
    thousandFormat (number) {
      return Float.thousandFormat(number)
    },
    oneDecimal (number) {
      return Float.trimZeroes(parseFloat(number).toFixed(1))
    },
    twoDecimal (number) {
      return Float.trimZeroes(parseFloat(number).toFixed(2))
    }
  },
  methods: {
    showDetailsEdit () {
      return !this.carItineraryHelper.isPickedUp() && this.hasManuallyCreatedShipments()
    },

    showShipmentDetailsEdit (shipment) {
      return this.showDetailsEdit() && shipment.isCreatedManually
    },

    hasManuallyCreatedShipments () {
      for (let i = 0; i < this.details.shipments.length; i++) {
        if (this.details.shipments[i].isCreatedManually) {
          return true
        }
      }
      return false
    },
    onPiecesInput (input) {
      for (let i = 0; i < this.details.shipments.length; i++) {
        if (this.details.shipments[i].id === input.shipmentId) {
          this.details.shipments[i].newPieces = parseInt(input.value)
        }
      }
    },
    onWeightInput (input) {
      for (let i = 0; i < this.details.shipments.length; i++) {
        if (this.details.shipments[i].id === input.shipmentId) {
          this.details.shipments[i].newWeight = parseFloat(input.value)
        }
      }
    },
    save () {
      const _self = this
      if (this.hasManuallyCreatedShipments()) {
        const shipmentDetails = []
        for (let i = 0; i < this.details.shipments.length; i++) {
          const shipment = this.details.shipments[i]
          const shipmentDetail = {}

          if (!this.lodash.isUndefined(shipment.newPieces) && this.lodash.isNumber(shipment.newPieces)) {
            shipmentDetail.piecesActual = shipment.newPieces
            shipmentDetail.piecesManifested = shipment.newPieces
          }

          if (!this.lodash.isUndefined(shipment.newWeight) && this.lodash.isNumber(shipment.newWeight)) {
            shipmentDetail.weightActual = shipment.newWeight
            shipmentDetail.weightManifested = shipment.newWeight
          }

          if (!this.lodash.isEmpty(shipmentDetail)) {
            shipmentDetail.shipmentId = shipment.id
            shipmentDetails.push(shipmentDetail)
          }
        }

        this.$showLoadingOverlay()
        const qStepApi = new QStepApi()

        const detailsUpdatePromise = qStepApi.updateCarShipmentDetails(this.carItinerary.car.id, shipmentDetails)

        detailsUpdatePromise.then(response => {
          if (response.data.success) {
            _self.updateDetails()
            _self.showCarShipmentDetailsUpdateSuccess()
          } else {
            _self.showCarShipmentDetailsUpdateFailure()
            this.$hideLoadingOverlay()
          }
          this.$hideLoadingOverlay()
        }).catch(() => {
          _self.showCarShipmentDetailsUpdateFailure()
          this.$hideLoadingOverlay()
        })
      }
    },
    unloadingConfirmed () {
      this.updateDetails()
    },
    unloadingCancelled () {
      this.updateDetails()
    },
    hasNotice () {
      return this.details.notice !== null && this.details.notice !== ''
    },
    showCarShipmentDetailsUpdateSuccess () {
      this.visibleCarShipmentDetailsUpdateSuccess = true
    },
    showCarShipmentDetailsUpdateFailure () {
      this.visibleCarShipmentDetailsUpdateFailure = true
    },
    updateDetails () {
      this.$showLoadingOverlay()
      const _self = this
      const qStepApi = new QStepApi()
      let pickUpDetailsPromise = null

      if (this.isPrivate()) {
        pickUpDetailsPromise = qStepApi.getPickUpDetails(this.carItineraryId)
      } else if (this.isPublic()) {
        pickUpDetailsPromise = qStepApi.getPickUpDetails(this.carItineraryId, this.encryptedData)
      }

      pickUpDetailsPromise
        .then(response => {
          if (response.data.data !== undefined) {
            const payload = response.data.data
            _self.carItinerary = payload
            if (payload.departureClient !== undefined && payload.departureClient !== null) {
              _self.details.pickUpAddress = payload.departureClient.company + ' - ' + payload.departureClient.address + ', ' + payload.departureClient.zipCode + ' ' + payload.departureClient.city
            }

            if (payload.arrivalClient !== undefined && payload.arrivalClient !== null) {
              _self.details.dropOffAddress = payload.arrivalClient.company + ' - ' + payload.arrivalClient.address + ', ' + payload.arrivalClient.zipCode + ' ' + payload.arrivalClient.city
            }

            if (payload.car.notice !== undefined && payload.car.notice !== null) {
              _self.details.notice = payload.car.notice
            }

            if (payload.car.shipments !== undefined && payload.car.shipments !== null) {
              _self.details.shipments = payload.car.shipments.map(shipment => {
                return {
                  id: shipment.id,
                  isCreatedManually: shipment.isCreatedManually,
                  awb: shipment.masterAwb,
                  pieces: shipment.piecesManifested,
                  weight: shipment.weightManifested,
                  volume: shipment.volumeManifested,
                  shcs: shipment.shcs.map(shc => {
                    if (shc.showInQDrive) {
                      return shc.code
                    }
                  })
                }
              })
            }

            if (payload.car.totals !== undefined && payload.car.totals !== null) {
              _self.details.shipmentTotals.pieces = payload.car.totals.piecesManifested
              _self.details.shipmentTotals.weight = payload.car.totals.weightManifested
              _self.details.shipmentTotals.volume = payload.car.totals.volumeManifested
            }
          }

          _self.doneRendering = true
          this.$hideLoadingOverlay()
        }).catch(() => {
          this.showDetailsFailed = true
          _self.doneRendering = true
          this.$hideLoadingOverlay()
        })
    }
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss">
    .car-itinerary-details {
        .driver-pick-up,
        .driver-unloading {
            text-align: center;
        }

        .sign-delivery {
            text-align: center;
            margin-bottom: 5px;
            margin-top: 5px;
        }

        .car-itinerary-details-footer {
            .btn-primary {
                display: none;
            }
        }

        .action-bar {
            margin-top: 20px;
            text-align: center;
        }

        .content {
            padding: 0px;
            text-align: left;
            font-size: 14px;

            .data-cell {
                padding-right: 0px;

                &.data-cell-edit {
                    height: 35px;
                }

                &.shipment-edit {
                    margin-top: 6px;
                }

                &.shc {
                    padding-left: 0px;
                }

                &.volume {
                    text-align: center;
                }
            }

            &.shipments {
                .header {
                    font-weight: bold;
                }

                .totals {
                    font-weight: bold;
                }
            }

            .name {
                font-weight: bold;
            }
        }
    }
</style>
